import React, { useState } from 'react';
import { styled, Theme, CSSObject, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Collapse,
    Divider,
    useMediaQuery
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StairsIcon from '@mui/icons-material/Stairs';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ClassIcon from '@mui/icons-material/Class';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ScienceIcon from '@mui/icons-material/Science';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HistoryIcon from '@mui/icons-material/History';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DrawIcon from '@mui/icons-material/Draw';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from './AuthContext';
import { useColorMode } from './ThemeContext';

const drawerWidth = 280;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: `calc(${theme.spacing(7)} + 1px)`, // Always keep the root width as the closed width
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
        ...(open && {
            ...openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
        }),
    },
}));



// ... (keep imports)

interface AppDrawerProps {
    open: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClose?: () => void;
}

const AppDrawer: React.FC<AppDrawerProps> = ({ open, onMouseEnter, onMouseLeave, onClose }) => {
    const [cadastrosOpen, setCadastrosOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [calculadorasOpen, setCalculadorasOpen] = useState(false);
    const [recibosOpen, setRecibosOpen] = useState(false);
    const [assinaturaOpen, setAssinaturaOpen] = useState(false);
    const { modulosAcessiveis } = useAuth();
    const colorMode = useColorMode();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const cadastroItems = [
        { text: 'Categorias', path: '/categorias', icon: <CategoryIcon /> },
        { text: 'Fornecedores', path: '/fornecedores', icon: <StoreIcon /> },
        { text: 'Investidores', path: '/investidores', icon: <PeopleIcon /> },
        { text: 'Fases da Obra', path: '/fases-de-obra', icon: <StairsIcon /> },
        { text: 'Itens', path: '/itens', icon: <ClassIcon /> },
    ];

    const drawerContent = (
        <>
            <Toolbar />
            <List>

                {modulosAcessiveis?.includes('Obras') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/obras" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><HomeWorkIcon /></ListItemIcon>
                            <ListItemText primary="Obras" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Lançamentos') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/lancamentos" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><MonetizationOnIcon /></ListItemIcon>
                            <ListItemText primary="Lançamentos" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Aportes e Resgates') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/aportes-resgates" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><AccountBalanceWalletIcon /></ListItemIcon>
                            <ListItemText primary="Aportes e Resgates" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Recibos') && (
                    <>
                        <ListItemButton onClick={() => setRecibosOpen(!recibosOpen)} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><ReceiptIcon /></ListItemIcon>
                            <ListItemText primary="Recibos" sx={{ opacity: open ? 1 : 0 }} />
                            {open && (recibosOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>
                        <Collapse in={recibosOpen && open} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItem disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton component={Link} to="/recibos/servico" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                        <ListItemIcon><AssignmentIcon /></ListItemIcon>
                                        <ListItemText primary="Serviço" />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton component={Link} to="/recibos/material" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                        <ListItemIcon><StoreIcon /></ListItemIcon>
                                        <ListItemText primary="Material" />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Collapse>
                    </>
                )}

                {modulosAcessiveis?.includes('Assinatura Digital') && (
                    <>
                        <ListItemButton onClick={() => setAssinaturaOpen(!assinaturaOpen)} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><DrawIcon /></ListItemIcon>
                            <ListItemText primary="Assinatura Digital" sx={{ opacity: open ? 1 : 0 }} />
                            {open && (assinaturaOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>
                        <Collapse in={assinaturaOpen && open} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItem disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton component={Link} to="/assinatura-digital/pendentes" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                        <ListItemIcon><PendingActionsIcon /></ListItemIcon>
                                        <ListItemText primary="Pendentes" />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton component={Link} to="/assinatura-digital/recibos" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                        <ListItemIcon><CheckCircleOutlineIcon /></ListItemIcon>
                                        <ListItemText primary="Recibos Assinados" />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Collapse>
                    </>
                )}

                {modulosAcessiveis?.includes('Unidades') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/unidades" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><HotelIcon /></ListItemIcon>
                            <ListItemText primary="Unidades" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Relatórios') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/relatorios" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><AssessmentIcon /></ListItemIcon>
                            <ListItemText primary="Relatórios" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Memória de Preço') && (
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton component={Link} to="/memoria-de-preco" onClick={isMobile ? onClose : undefined} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><QueryStatsIcon /></ListItemIcon>
                            <ListItemText primary="Memória de Preço" sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                )}

                {modulosAcessiveis?.includes('Calculadoras') && (
                    <>
                        <ListItemButton onClick={() => setCalculadorasOpen(!calculadorasOpen)} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><ScienceIcon /></ListItemIcon>
                            <ListItemText primary="Calculadoras" sx={{ opacity: open ? 1 : 0 }} />
                            {open && (calculadorasOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>
                        <Collapse in={calculadorasOpen && open} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {modulosAcessiveis?.includes('Atualização Monetária') && (
                                    <ListItem disablePadding sx={{ display: 'block' }}>
                                        <ListItemButton component={Link} to="/calculadoras/atualizacao-monetaria" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                            <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                                            <ListItemText primary="Atualização Monetária" />
                                        </ListItemButton>
                                    </ListItem>
                                )}
                                {modulosAcessiveis?.includes('INSS de Obra') && (
                                    <ListItem disablePadding sx={{ display: 'block' }}>
                                        <ListItemButton component={Link} to="/calculadoras/inss-obra" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                            <ListItemIcon><ApartmentIcon /></ListItemIcon>
                                            <ListItemText primary="INSS de Obra" />
                                        </ListItemButton>
                                    </ListItem>
                                )}
                            </List>
                        </Collapse>
                    </>
                )}

                <ListItemButton onClick={() => setCadastrosOpen(!cadastrosOpen)} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                    <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><AssignmentIcon /></ListItemIcon>
                    <ListItemText primary="Cadastros" sx={{ opacity: open ? 1 : 0 }} />
                    {open && (cadastrosOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
                <Collapse in={cadastrosOpen && open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {cadastroItems.map((item) => (
                            modulosAcessiveis?.includes(item.text) && (
                                <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton component={Link} to={item.path} onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </ListItemButton>
                                </ListItem>
                            )
                        ))}
                    </List>
                </Collapse>

                {(modulosAcessiveis?.includes('Administração') ||
                    modulosAcessiveis?.includes('Módulos') ||
                    modulosAcessiveis?.includes('Perfis') ||
                    modulosAcessiveis?.includes('Auditoria')) && (
                        <>
                            <ListItemButton onClick={() => setAdminOpen(!adminOpen)} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}><AdminPanelSettingsIcon /></ListItemIcon>
                                <ListItemText primary="Administração" sx={{ opacity: open ? 1 : 0 }} />
                                {open && (adminOpen ? <ExpandLess /> : <ExpandMore />)}
                            </ListItemButton>
                            <Collapse in={adminOpen && open} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {modulosAcessiveis?.includes('Administração') && (
                                        <ListItem disablePadding sx={{ display: 'block' }}>
                                            <ListItemButton component={Link} to="/admin" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                                <ListItemIcon><PeopleIcon /></ListItemIcon>
                                                <ListItemText primary="Gerenciar Usuários" />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                    {modulosAcessiveis?.includes('Módulos') && (
                                        <ListItem disablePadding sx={{ display: 'block' }}>
                                            <ListItemButton component={Link} to="/admin/modulos" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                                <ListItemIcon><CategoryIcon /></ListItemIcon>
                                                <ListItemText primary="Gerenciar Módulos" />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                    {modulosAcessiveis?.includes('Perfis') && (
                                        <ListItem disablePadding sx={{ display: 'block' }}>
                                            <ListItemButton component={Link} to="/admin/perfis" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                                <ListItemIcon><AssignmentIcon /></ListItemIcon>
                                                <ListItemText primary="Gerenciar Perfis" />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                    {modulosAcessiveis?.includes('Administração') && (
                                        <ListItem disablePadding sx={{ display: 'block' }}>
                                            <ListItemButton component={Link} to="/admin/chaves-api" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                                <ListItemIcon><VpnKeyIcon /></ListItemIcon>
                                                <ListItemText primary="Gerenciar Chaves API" />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                    {modulosAcessiveis?.includes('Auditoria') && (
                                        <ListItem disablePadding sx={{ display: 'block' }}>
                                            <ListItemButton component={Link} to="/admin/auditoria" onClick={isMobile ? onClose : undefined} sx={{ pl: 4 }}>
                                                <ListItemIcon><HistoryIcon /></ListItemIcon>
                                                <ListItemText primary="Auditoria" />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                </List>
                            </Collapse>
                        </>
                    )}
            </List>
            <Divider />
            <List>
                <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton onClick={colorMode.toggleColorMode} sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}>
                        <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}>
                            {colorMode.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        </ListItemIcon>
                        <ListItemText primary={colorMode.mode === 'dark' ? 'Modo Claro' : 'Modo Escuro'} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                </ListItem>
            </List>
        </>
    );

    if (isMobile) {
        return (
            <MuiDrawer
                variant="temporary"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                }}
            >
                {drawerContent}
            </MuiDrawer>
        );
    }

    return (
        <Drawer
            variant="permanent"
            open={open}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            sx={{
                display: { xs: 'none', md: 'block' },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default AppDrawer;